// Thresholds for dynamic search icon
export const SINGLE_RESULT_CHAR_THRESHOLD = 3;
export const NATURAL_LANGUAGE_WORD_THRESHOLD = 3;

export const UPLOAD_GATE_REASON = "Log in to upload";

// filterName used to mark the search pill for a natural language query
export const NATURAL_LANGUAGE_FILTER_NAME = "naturalLanguage";

// Display config for chips rendered in the search-filter strip for non-entity
// filters (dateRange, tags, genres, instruments, aggregate, naturalLanguage)
export const SEARCH_CHIP_CONFIG = {
  dateRange: { iconName: "tag", displayName: "Date Range" },
  tags: { iconName: "tag", displayName: "Tag" },
  genres: { iconName: "tag", displayName: "Genre" },
  instruments: { iconName: "instrument", displayName: "Instrument" },
  aggregate: { iconName: "quick_filter", displayName: "Quick Filters" },
  [NATURAL_LANGUAGE_FILTER_NAME]: {
    iconName: "natural_language",
    displayName: "Natural Language"
  }
};

export const SEARCH_MODES = Object.freeze({
  SEARCH: "search",
  SIMILARITY: "similarity"
});

// views options of track dropdown (SingleTrackView, per-row TrackDropdown)
export const TRACK_VIEW = Object.freeze({
  VARIATION: "variation",
  SIMILAR: "similar_tracks"
});

// Hint text to show only when we're in classic search mode
// Interleaved: track title → tags/instruments → natural language (repeat)
const SEARCH_HINTS = [
  {
    placeholder: "Trek on Alpus 2",
    hint: "Search for a track, album, or playlist",
    link: null
  },
  {
    placeholder: "Piano, ambient, lofi",
    hint: "Search and filter by instruments, tags, and genres",
    link: null
  },
  {
    placeholder: "A quiet afternoon at the beach",
    hint: "You can search with natural language",
    link: null
  },
  {
    placeholder: "Running With the Wind",
    hint: "Search for a track, album, or playlist",
    link: null
  },
  {
    placeholder: "Acoustic guitar, folk, uplifting",
    hint: "Combine genres, moods, and instruments in one search",
    link: null
  },
  {
    placeholder: "A high-stakes chase through city streets",
    hint: "Describe your scene and we'll find the right track",
    link: null
  },
  {
    placeholder: "Late Night Drive",
    hint: "Search for a track, album, or playlist",
    link: null
  },
  {
    placeholder: "Synth, electronic, driving",
    hint: "Mix any combination of tags, genres, and instruments",
    link: null
  },
  {
    placeholder: "A heartwarming moment at the end of a film",
    hint: "Natural language search understands scenes and moods",
    link: null
  },
  {
    placeholder: "Golden Hour",
    hint: "Search for a track, album, or playlist",
    link: null
  },
  {
    placeholder: "Strings, cinematic, bittersweet",
    hint: "Filter by instruments, genres, moods, and more",
    link: null
  },
  {
    placeholder: "Triumphant and soaring, like a hero's return",
    hint: "Describe a feeling and we'll match it",
    link: null
  }
];

// Hint text to show only when we're in similarity search mode
const SIMILARITY_HINTS = [
  {
    placeholder: "open.spotify.com/track/0eP…",
    hint: "Paste a link to find acoustically similar tracks in seconds",
    link: null
  },
  {
    placeholder: "https://youtu.be/0FyhuhA_z2c",
    hint: "Paste a link to find acoustically similar tracks in seconds",
    link: null
  },
  {
    placeholder: "open.spotify.com/track/1dGr1cChk…",
    hint: "Paste a Spotify link to search by audio",
    link: null
  },
  {
    placeholder: "https://www.youtube.com/watch?v=…",
    hint: "Drop in a YouTube link to find similar music instantly",
    link: null
  },
  {
    placeholder: "music.apple.com/us/album/golden-hour/…",
    hint: "Apple Music, Spotify, YouTube, and TikTok links all work",
    link: null
  },
  {
    placeholder: "music.apple.com/us/song/late-night-drive/…",
    hint: "Paste an Apple Music link to search by sound",
    link: null
  },
  {
    placeholder: "tiktok.com/@artist/video/71234567890",
    hint: "Heard something on TikTok? Paste the link and find it here",
    link: null
  },
  {
    placeholder: "vm.tiktok.com/ZMxxxxxx/",
    hint: "TikTok, YouTube, Spotify, and Apple Music links all work",
    link: null
  }
];

// Hint text that works for either mode
const COMMON_HINTS = [
  {
    placeholder: "myfavoritejam.mp3",
    hint: "Upload any track to search for similar songs in our library",
    link: { label: "Try it now", action: { type: "openUpload" } }
  },
  {
    placeholder: "reference_track.mp3",
    hint: "Have a reference track? Search by audio —",
    link: { label: "Upload it here", action: { type: "openUpload" } }
  },
  {
    placeholder: "film_score_ref.mp3",
    hint: "Match the vibe of any reference recording",
    link: { label: "Try it now", action: { type: "openUpload" } }
  },
  {
    placeholder: "my_inspiration.wav",
    hint: "Can't put it into words? Let the audio do the searching —",
    link: { label: "Upload your own", action: { type: "openUpload" } }
  },
  {
    placeholder: "podcast_intro.mp3",
    hint: "Find music that matches your existing audio —",
    link: { label: "Upload it here", action: { type: "openUpload" } }
  },
  {
    placeholder: "demo_recording.mp3",
    hint: "Search by sound, not just words",
    link: { label: "Try it now", action: { type: "openUpload" } }
  },
  {
    placeholder: "scene_ref.mp3",
    hint: "Upload a reference and we'll find the closest match",
    link: { label: "Upload your own", action: { type: "openUpload" } }
  },
  {
    placeholder: "sound_i_love.mp3",
    hint: "The fastest way to find what you're hearing",
    link: { label: "Try it now", action: { type: "openUpload" } }
  }
];

const HINTS_PER_POOL = 2;

const MODE_HINTS = {
  [SEARCH_MODES.SEARCH]: SEARCH_HINTS,
  [SEARCH_MODES.SIMILARITY]: SIMILARITY_HINTS
};

// Return n randomly-selected items from arr (in random order)
const sample = (arr, n) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a.slice(0, n);
};

// Build a fresh random hint set: HINTS_PER_POOL mode-specific + HINTS_PER_POOL common
export const buildHints = (mode, { includeUploadHints = true } = {}) =>
  sample(
    includeUploadHints
      ? [
          ...sample(MODE_HINTS[mode], HINTS_PER_POOL),
          ...sample(COMMON_HINTS, HINTS_PER_POOL)
        ]
      : sample(MODE_HINTS[mode], HINTS_PER_POOL * 2),
    HINTS_PER_POOL * 2
  );

export const HINT_CYCLE_MS = 4500;

// Uploaded analyzing properties
export const ANALYZING_CYCLE_MS = 2200;

export const ANALYZING_LABELS = ["Analyzing...", "Finding similar tracks..."];
